import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFetcherPublic } from '@app/hooks/fetcher';
import { getSetting, useSetting } from '@app/hooks/user/mysetting';
import { SETTING_TYPE_SYSTEM } from '@app/constants/SETTING';

const currencyIconUrl = '/assets/newGNB/icon-currency.svg';

const SelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  position: relative;
`;

const IconWrapper = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CurrencyIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const LabelText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const DropdownBridge = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 200px;
  height: 8px;
  z-index: 1001;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #0d1117;
  border-radius: 12px;
  width: 200px;
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  flex-direction: column;
  padding: 20px 12px;
  z-index: 1001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: ${(props) => (props.$isOpen ? 'auto' : 'none')};
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px;
  margin-bottom: 0;
`;

const MenuHeaderText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const MenuItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const MenuItemText = styled.p<{ $isSelected: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.$isSelected ? '#ff8a00' : '#b3b5bd')};
  letter-spacing: -0.28px;
  margin: 0;
`;

const CurrencySelector: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: setting, updateSetting } = useSetting(SETTING_TYPE_SYSTEM);
  const { data: result } = useFetcherPublic('/system/currency');
  const currency = getSetting(setting, 'CURRENCY', 'USD');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currencyOptions = result?.result?.result || [];

  // 화폐 이름 매핑 (Figma 디자인에 맞춤)
  const currencyNameMap: { [key: string]: string } = {
    USD: 'USD Doller',
    KRW: '₩(KRW)',
    EUR: 'EUR',
    HKD: 'HKD',
    JPY: 'YEN',
  };

  // USD, KRW, EUR, HKD, JPY만 표시
  const availableCurrencies = currencyOptions
    .filter((item: any) =>
      ['USD', 'KRW', 'EUR', 'HKD', 'JPY'].includes(item.code)
    )
    .map((item: any) => ({
      code: item.code,
      name: currencyNameMap[item.code] || item.name,
    }));

  const handleCurrencyChange = (currencyCode: string) => {
    updateSetting('CURRENCY', currencyCode);
    setIsOpen(false);
  };

  return (
    <SelectorContainer
      ref={dropdownRef}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <IconWrapper>
        <CurrencyIcon src={currencyIconUrl} alt="currency" />
      </IconWrapper>
      <LabelText>{t('Currency')}</LabelText>
      {isOpen && <DropdownBridge />}
      <DropdownMenu $isOpen={isOpen}>
        <MenuHeader>
          <IconWrapper>
            <CurrencyIcon src={currencyIconUrl} alt="currency" />
          </IconWrapper>
          <MenuHeaderText>{t('Currency')}</MenuHeaderText>
        </MenuHeader>
        {availableCurrencies.map((curr) => (
          <MenuItem
            key={curr.code}
            $isSelected={currency === curr.code}
            onClick={() => handleCurrencyChange(curr.code)}
          >
            <MenuItemText $isSelected={currency === curr.code}>
              {curr.name}
            </MenuItemText>
          </MenuItem>
        ))}
      </DropdownMenu>
    </SelectorContainer>
  );
};

export default CurrencySelector;
